/**
 * Request Submitted Dialog — W3-A · B7 · workflow 202 confirmation.
 *
 * Rendered by ProjectsList (and by the my-work surface) after
 * POST /api/projects/engagement returns HTTP 202 Accepted.  Shows:
 *   • Success acknowledgement
 *   • Status: Pending Approval
 *   • Workflow / request reference
 *   • What happens next
 *   • Link to My Requests
 *
 * Deliberately avoids any wording that implies the Project is now
 * active — the Active Project only exists once an approver acts.
 */
import { Link } from "react-router-dom";
import { CheckCircle, Clock, ArrowRight } from "@phosphor-icons/react";

import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";


export default function RequestSubmittedDialog({ open, request, onClose }) {
    if (!request) return null;
    const submittedAt = request.created_at
        ? new Date(request.created_at).toLocaleString()
        : "just now";
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent
                className="max-w-lg"
                data-testid="request-submitted-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <CheckCircle className="w-6 h-6 text-emerald-600" weight="fill" />
                        Request submitted successfully
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4">
                    <div
                        className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
                        data-testid="request-status-pending"
                    >
                        <Clock className="w-5 h-5 text-amber-600 mt-0.5" weight="fill" />
                        <div className="text-sm">
                            <div className="font-semibold text-amber-900">
                                Status · Pending Approval
                            </div>
                            <div className="text-amber-800">
                                Your Project Request has been queued
                                for review.  No Active Project or
                                engagement number is created until an
                                authorised approver approves it.
                            </div>
                        </div>
                    </div>

                    <div className="rounded-md border bg-slate-50 px-4 py-3 space-y-1 text-sm">
                        <div className="flex justify-between">
                            <span className="text-slate-500">
                                Request reference
                            </span>
                            <code
                                className="font-mono text-slate-800"
                                data-testid="request-workflow-instance-id"
                            >
                                {request.workflow_instance_id}
                            </code>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">
                                Status
                            </span>
                            <span className="text-slate-800">
                                Pending Approval
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">
                                Submitted
                            </span>
                            <span className="text-slate-800">
                                {submittedAt}
                            </span>
                        </div>
                    </div>

                    <div className="text-sm text-slate-600 space-y-1">
                        <div className="font-semibold text-slate-700">
                            What happens next
                        </div>
                        <ol className="list-decimal list-inside space-y-0.5 pl-1">
                            <li>An authorised approver reviews your request in the Approvals Queue.</li>
                            <li>If approved, the Active Project is created and an engagement number is allocated.</li>
                            <li>If rejected, you will see the reviewer&apos;s reason on your request card.</li>
                            <li>You can withdraw your request at any time while it is pending.</li>
                        </ol>
                    </div>
                </div>

                <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        data-testid="request-submitted-close-btn"
                    >
                        Close
                    </Button>
                    <Button asChild data-testid="request-submitted-my-requests-btn">
                        <Link
                            to="/workflows/my-requests"
                            onClick={onClose}
                        >
                            Go to My Project Requests
                            <ArrowRight className="w-4 h-4 ml-1" />
                        </Link>
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
